import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { resolveRowFormulas } from '../utils/sheetFormula';
import { userService } from './userService';

const SHEET_FORMS_COLLECTION = 'docs-sheet-forms';
const SHEET_FORM_DRAFTS_COLLECTION = 'docs-sheet-form-drafts';
const SHEETS_COLLECTION = 'docs-sheets';
const SHEET_ITEMS_SUBCOLLECTION = 'sheet-items';
const ACTIVE_ACTIONS_HEADER_ID = 'system_active_actions';
const SHEET_FORM_FIELD_WIDTH_VALUES = ['full', 'two-thirds', 'half', 'third'];
const SHEET_FORM_TEXT_ALIGN_VALUES = ['left', 'center', 'right'];
const SHEET_FORM_SLIDE_MODE_VALUES = ['manual', 'auto'];
const SHEET_RENDER_FONT_VALUES = new Set([
  'Inter',
  'Roboto',
  'Open Sans',
  'Montserrat',
  'Poppins',
  'Lato',
  'Nunito',
  'Source Sans Pro',
  'Raleway',
  'Ubuntu',
  'Playfair Display',
  'Merriweather',
  'Georgia',
  'Times New Roman',
  'Crimson Text'
]);
const SHEET_THEME_COLOR_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const sanitizeAccessMode = (value) => (
  `${value || ''}`.trim() === 'internal' ? 'internal' : 'public'
);

const sanitizeSheetVisibility = (value) => {
  const normalizedValue = `${value || ''}`.trim().toLowerCase();
  if (normalizedValue === 'restricted' || normalizedValue === 'private') {
    return normalizedValue;
  }
  return 'public';
};

const getEffectiveFormAccessMode = (_sheetVisibility, requestedAccessMode) => sanitizeAccessMode(requestedAccessMode);

const stripUndefinedDeep = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === 'object') {
    if (value.constructor !== Object) return value;
    const output = {};
    Object.entries(value).forEach(([key, item]) => {
      const cleaned = stripUndefinedDeep(item);
      if (cleaned !== undefined) output[key] = cleaned;
    });
    return output;
  }

  return value === undefined ? undefined : value;
};

const isEligibleSheetHeader = (header) => {
  if (!header || typeof header !== 'object') return false;
  if (`${header.id || ''}`.trim() === ACTIVE_ACTIONS_HEADER_ID) return false;
  if (`${header.systemField || ''}`.trim()) return false;
  if (`${header.type || ''}`.trim() === 'profile') return false;
  if (`${header.formula || ''}`.trim()) return false;
  return Boolean(`${header.id || ''}`.trim() && `${header.label || ''}`.trim());
};

const normalizeHeaderIds = (headerIds = [], validHeaderIds = []) => {
  const validSet = new Set(validHeaderIds);
  return Array.from(new Set(
    (Array.isArray(headerIds) ? headerIds : [])
      .map((headerId) => `${headerId || ''}`.trim())
      .filter((headerId) => headerId && validSet.has(headerId))
  ));
};

const normalizeFormFieldWidth = (value) => (
  SHEET_FORM_FIELD_WIDTH_VALUES.includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'full'
);

const normalizeRepeatableFieldWidths = (widths = {}, headerIds = []) => {
  const headerIdSet = new Set((Array.isArray(headerIds) ? headerIds : []).map((headerId) => `${headerId || ''}`.trim()).filter(Boolean));
  const sourceWidths = widths && typeof widths === 'object' && !Array.isArray(widths) ? widths : {};
  return Object.entries(sourceWidths).reduce((acc, [headerId, width]) => {
    const normalizedHeaderId = `${headerId || ''}`.trim();
    if (normalizedHeaderId && (!headerIdSet.size || headerIdSet.has(normalizedHeaderId))) {
      acc[normalizedHeaderId] = normalizeFormFieldWidth(width);
    }
    return acc;
  }, {});
};

const normalizeTextAlign = (value) => (
  SHEET_FORM_TEXT_ALIGN_VALUES.includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'left'
);

const normalizeSlideMode = (value) => (
  SHEET_FORM_SLIDE_MODE_VALUES.includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'manual'
);
const normalizeSheetRenderFont = (value) => (
  SHEET_RENDER_FONT_VALUES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'Inter'
);
const normalizeSheetThemeColor = (value) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized || !SHEET_THEME_COLOR_PATTERN.test(normalized)) return null;
  return normalized.startsWith('#') ? normalized : `#${normalized}`;
};
const normalizeFormLogoUrl = (value) => {
  const normalized = `${value || ''}`.trim();
  return normalized || null;
};
const normalizeFormOgImageUrl = (value) => {
  const normalized = `${value || ''}`.trim();
  return normalized || null;
};
const normalizeAvailabilityDateTime = (value) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return null;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};
const getFormAvailability = (config = {}) => {
  const availabilityStartAt = normalizeAvailabilityDateTime(config?.availabilityStartAt);
  const availabilityEndAt = normalizeAvailabilityDateTime(config?.availabilityEndAt);
  if (availabilityStartAt && availabilityEndAt && new Date(availabilityStartAt) > new Date(availabilityEndAt)) {
    throw new Error('Form availability start must be before the end.');
  }
  return {
    availabilityStartAt: availabilityStartAt || undefined,
    availabilityEndAt: availabilityEndAt || undefined
  };
};
const getFormAvailabilityState = (formData = {}, now = new Date()) => {
  const availabilityStartAt = normalizeAvailabilityDateTime(formData?.availabilityStartAt);
  const availabilityEndAt = normalizeAvailabilityDateTime(formData?.availabilityEndAt);
  const currentTime = now.getTime();
  if (availabilityStartAt && currentTime < new Date(availabilityStartAt).getTime()) return 'not_started';
  if (availabilityEndAt && currentTime > new Date(availabilityEndAt).getTime()) return 'ended';
  return 'available';
};

const normalizeSlideIntervalSeconds = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 5;
  return Math.max(2, Math.min(30, Math.round(numeric)));
};

const normalizeSlideItems = (slides = []) => (
  (Array.isArray(slides) ? slides : [])
    .map((slide, index) => {
      const url = `${slide?.url || slide?.src || ''}`.trim();
      if (!url) return null;
      return {
        id: `${slide?.id || `slide_${index + 1}`}`.trim() || `slide_${index + 1}`,
        url,
        alt: `${slide?.alt || slide?.caption || ''}`.trim()
      };
    })
    .filter(Boolean)
);

const createFieldLayoutItem = (headerId, width = 'full') => ({
  id: `field_${headerId}`,
  type: 'field',
  headerId,
  width: normalizeFormFieldWidth(width)
});

const normalizeSectionDividerLabel = (item = {}) => {
  const label = item.label ?? item.content ?? 'Section';
  const normalizedLabel = `${label}`;
  return normalizedLabel.trim() ? normalizedLabel : 'Section';
};

const sanitizeSubmissionMode = (value) => (
  `${value || ''}`.trim() === 'modular' ? 'modular' : 'singleRow'
);

const normalizeRepeatableHeaderIds = (headerIds = [], validHeaderIds = [], excludedHeaderIds = []) => {
  const validSet = new Set(validHeaderIds);
  const excludedSet = new Set(excludedHeaderIds);
  return Array.from(new Set(
    (Array.isArray(headerIds) ? headerIds : [])
      .map((headerId) => `${headerId || ''}`.trim())
      .filter((headerId) => headerId && validSet.has(headerId) && !excludedSet.has(headerId))
  ));
};

const normalizeRepeatableRequiredHeaderIds = (headerIds = [], repeatableHeaderIds = []) => {
  const repeatableSet = new Set(repeatableHeaderIds);
  return Array.from(new Set(
    (Array.isArray(headerIds) ? headerIds : [])
      .map((headerId) => `${headerId || ''}`.trim())
      .filter((headerId) => headerId && repeatableSet.has(headerId))
  ));
};

const normalizeMinimumRepeatableRows = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(1, Math.min(25, Math.round(numeric)));
};

const sanitizeRepeatableEntryMode = (value) => (
  `${value || ''}`.trim() === 'allAtOnce' ? 'allAtOnce' : 'stepper'
);

const normalizeRepeatablePromptText = (value, fallback) => `${value || ''}`.trim() || fallback;
const buildSheetFormDraftId = (formId, userId) => `${formId}_${userId}`;
const normalizeFormPrefillSelectorConfig = (config = {}) => ({
  enabled: config?.enabled === true,
  sourceSheetId: `${config?.sourceSheetId || ''}`.trim(),
  actionId: `${config?.actionId || ''}`.trim()
});

const buildDefaultModules = (headers = []) => ([
  {
    id: 'shared_fields',
    kind: 'group',
    title: 'Shared Fields',
    repeatable: false,
    fieldIds: getDefaultEnabledHeaderIds(headers)
  },
  {
    id: 'detail_rows',
    kind: 'group',
    title: 'Detail Rows',
    repeatable: true,
    fieldIds: [],
    repeatConfig: {
      entryMode: 'stepper',
      minRows: 1,
      addPrompt: 'Add another row?',
      addLabel: 'Add another row',
      doneLabel: 'Continue'
    }
  }
]);

const normalizeModules = (modules = [], headers = [], fallbackConfig = {}) => {
  const eligibleHeaderIds = (Array.isArray(headers) ? headers : [])
    .filter((header) => isEligibleSheetHeader(header))
    .map((header) => header.id);
  const hasExplicitSharedFieldIds = Array.isArray(fallbackConfig?.enabledHeaderIds);
  const hasExplicitRepeatableFieldIds = Array.isArray(fallbackConfig?.repeatableHeaderIds);
  const sharedFieldIds = normalizeHeaderIds(
    fallbackConfig?.enabledHeaderIds,
    eligibleHeaderIds
  );
  const repeatableFieldIds = normalizeRepeatableHeaderIds(
    fallbackConfig?.repeatableHeaderIds,
    eligibleHeaderIds,
    sharedFieldIds
  );
  const sourceModules = Array.isArray(modules) && modules.length
    ? modules
    : buildDefaultModules(headers).map((module) => (
        module.repeatable
          ? {
              ...module,
              fieldIds: repeatableFieldIds,
              repeatConfig: {
                ...module.repeatConfig,
                minRows: normalizeMinimumRepeatableRows(fallbackConfig?.minimumRepeatableRows),
                addPrompt: normalizeRepeatablePromptText(fallbackConfig?.repeatableAddPrompt, 'Add another row?'),
                addLabel: normalizeRepeatablePromptText(fallbackConfig?.repeatableAddLabel, 'Add another row'),
                doneLabel: normalizeRepeatablePromptText(fallbackConfig?.repeatableDoneLabel, 'Continue')
              }
            }
          : {
              ...module,
              fieldIds: sharedFieldIds
            }
      ));

  const sharedModule = sourceModules.find((module) => module && module.repeatable !== true);
  const repeatableModule = sourceModules.find((module) => module && module.repeatable === true);
  const normalizedSharedModuleFieldIds = normalizeHeaderIds(sharedModule?.fieldIds, eligibleHeaderIds);
  const normalizedRepeatableModuleFieldIds = normalizeRepeatableHeaderIds(
    repeatableModule?.fieldIds,
    eligibleHeaderIds,
    sharedFieldIds
  );
  const preferredEntryMode = fallbackConfig?.repeatableEntryMode ?? repeatableModule?.repeatConfig?.entryMode;
  const preferredMinRows = fallbackConfig?.minimumRepeatableRows ?? repeatableModule?.repeatConfig?.minRows;
  const preferredAddPrompt = `${fallbackConfig?.repeatableAddPrompt ?? ''}`.trim()
    || `${repeatableModule?.repeatConfig?.addPrompt ?? ''}`.trim();
  const preferredAddLabel = `${fallbackConfig?.repeatableAddLabel ?? ''}`.trim()
    || `${repeatableModule?.repeatConfig?.addLabel ?? ''}`.trim();
  const preferredDoneLabel = `${fallbackConfig?.repeatableDoneLabel ?? ''}`.trim()
    || `${repeatableModule?.repeatConfig?.doneLabel ?? ''}`.trim();

  return [
    {
      id: `${sharedModule?.id || 'shared_fields'}`.trim() || 'shared_fields',
      kind: 'group',
      title: `${sharedModule?.title || 'Shared Fields'}`.trim() || 'Shared Fields',
      repeatable: false,
      fieldIds: hasExplicitSharedFieldIds
        ? sharedFieldIds
        : normalizedSharedModuleFieldIds.length || !sharedFieldIds.length
        ? normalizedSharedModuleFieldIds
        : sharedFieldIds
    },
    {
      id: `${repeatableModule?.id || 'detail_rows'}`.trim() || 'detail_rows',
      kind: 'group',
      title: `${repeatableModule?.title || 'Detail Rows'}`.trim() || 'Detail Rows',
      repeatable: true,
      fieldIds: hasExplicitRepeatableFieldIds
        ? repeatableFieldIds
        : normalizedRepeatableModuleFieldIds.length || !repeatableFieldIds.length
        ? normalizedRepeatableModuleFieldIds
        : repeatableFieldIds,
      repeatConfig: {
        entryMode: sanitizeRepeatableEntryMode(preferredEntryMode),
        minRows: normalizeMinimumRepeatableRows(preferredMinRows),
        addPrompt: normalizeRepeatablePromptText(preferredAddPrompt, 'Add another row?'),
        addLabel: normalizeRepeatablePromptText(preferredAddLabel, 'Add another row'),
        doneLabel: normalizeRepeatablePromptText(preferredDoneLabel, 'Continue')
      }
    }
  ];
};

const getDefaultFormLayout = (headers = []) => (
  getDefaultEnabledHeaderIds(headers).map((headerId) => createFieldLayoutItem(headerId))
);

const normalizeFormLayoutItems = (layout = [], headers = [], fallbackEnabledHeaderIds = [], fallbackRequiredHeaderIds = [], requireFieldFallback = true) => {
  const eligibleHeaders = (Array.isArray(headers) ? headers : []).filter((header) => isEligibleSheetHeader(header));
  const eligibleHeaderIds = eligibleHeaders.map((header) => header.id);
  const eligibleHeaderIdSet = new Set(eligibleHeaderIds);
  const fallbackSourceHeaderIds = !requireFieldFallback && Array.isArray(fallbackEnabledHeaderIds)
    ? fallbackEnabledHeaderIds
    : Array.isArray(fallbackEnabledHeaderIds) && fallbackEnabledHeaderIds.length
      ? fallbackEnabledHeaderIds
      : eligibleHeaderIds;
  const fallbackFieldIds = normalizeHeaderIds(fallbackSourceHeaderIds, eligibleHeaderIds);
  const requiredHeaderIds = normalizeHeaderIds(fallbackRequiredHeaderIds || [], fallbackFieldIds);
  const requiredHeaderIdSet = new Set(requiredHeaderIds);
  const sourceLayout = Array.isArray(layout) && layout.length
    ? layout
    : fallbackFieldIds.map((headerId) => createFieldLayoutItem(headerId));
  const seenFieldIds = new Set();
  const normalizedLayout = [];

  sourceLayout.forEach((item, index) => {
    const type = `${item?.type || ''}`.trim();
    if (type === 'field') {
      const headerId = `${item?.headerId || item?.fieldId || item?.id || ''}`.trim();
      if (!headerId || !eligibleHeaderIdSet.has(headerId) || seenFieldIds.has(headerId)) return;
      seenFieldIds.add(headerId);
      normalizedLayout.push({
        id: `${item?.id || `field_${headerId}`}`.trim() || `field_${headerId}`,
        type: 'field',
        headerId,
        width: normalizeFormFieldWidth(item?.width || item?.formWidth),
        required: requiredHeaderIdSet.has(headerId)
      });
      return;
    }

    if (type === 'sectionDivider') {
      normalizedLayout.push({
        id: `${item?.id || `section_${index + 1}`}`.trim() || `section_${index + 1}`,
        type: 'sectionDivider',
        label: normalizeSectionDividerLabel(item)
      });
      return;
    }

    if (type === 'textBlock') {
      normalizedLayout.push({
        id: `${item?.id || `text_block_${index + 1}`}`.trim() || `text_block_${index + 1}`,
        type: 'textBlock',
        contentHtml: `${item?.contentHtml || item?.content || ''}`,
        alignment: normalizeTextAlign(item?.alignment)
      });
      return;
    }

    if (type === 'imageSlide') {
      normalizedLayout.push({
        id: `${item?.id || `image_slide_${index + 1}`}`.trim() || `image_slide_${index + 1}`,
        type: 'imageSlide',
        slides: normalizeSlideItems(item?.slides),
        slideMode: normalizeSlideMode(item?.slideMode),
        autoAdvanceSeconds: normalizeSlideIntervalSeconds(item?.autoAdvanceSeconds)
      });
      return;
    }

    if (type === 'pageBreak') {
      normalizedLayout.push({
        id: `${item?.id || `page_break_${index + 1}`}`.trim() || `page_break_${index + 1}`,
        type: 'pageBreak'
      });
    }
  });

  if (requireFieldFallback && !normalizedLayout.some((item) => item.type === 'field')) {
    return fallbackFieldIds.map((headerId) => ({
      ...createFieldLayoutItem(headerId),
      required: requiredHeaderIdSet.has(headerId)
    }));
  }

  return normalizedLayout;
};

const getDefaultEnabledHeaderIds = (headers = []) => (
  headers
    .filter((header) => isEligibleSheetHeader(header))
    .map((header) => header.id)
);

const formatDefaultDateTokenValue = (headerType, date = new Date()) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  switch (headerType) {
    case 'date':
      return `${year}-${month}-${day}`;
    case 'time':
      return `${hours}:${minutes}`;
    case 'datetime':
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    default:
      return '';
  }
};

const getDynamicDefaultTokenForHeader = (headerType, rawValue) => {
  const normalizedValue = `${rawValue ?? ''}`.trim().toUpperCase();
  if (headerType === 'date' && normalizedValue === 'TODAY()') return 'TODAY()';
  if (headerType === 'time' && normalizedValue === 'TIMENOW()') return 'TIMENOW()';
  if (headerType === 'datetime' && normalizedValue === 'NOW()') return 'NOW()';
  return '';
};

const getFieldInitialValue = (header) => {
  if (!header || header.defaultValueMode === 'none') {
    if (header?.type === 'multiselect' || header?.type === 'list') return [];
    if (header?.type === 'checkbox') return false;
    return '';
  }

  if (header.type === 'checkbox') return Boolean(header.defaultValue);
  if (header.type === 'multiselect' || header.type === 'list') {
    return Array.isArray(header.defaultValue) ? header.defaultValue : [];
  }

  const dynamicToken = getDynamicDefaultTokenForHeader(header.type, header.defaultValue);
  if (dynamicToken) return formatDefaultDateTokenValue(header.type);
  return header.defaultValue ?? '';
};

const sanitizeSubmissionValues = (values = {}, headers = []) => {
  const rawValues = (Array.isArray(headers) ? headers : []).reduce((acc, header) => {
    const headerId = `${header?.id || ''}`.trim();
    if (!headerId) return acc;

    if (header.type === 'checkbox') {
      acc[headerId] = Boolean(values?.[headerId]);
      return acc;
    }

    if (header.type === 'multiselect' || header.type === 'list') {
      acc[headerId] = Array.isArray(values?.[headerId])
        ? values[headerId].map((item) => `${item || ''}`.trim()).filter(Boolean)
        : `${values?.[headerId] || ''}`
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      return acc;
    }

    if (header.type === 'number' || header.type === 'rating' || header.type === 'currency' || header.type === 'percent') {
      const raw = values?.[headerId];
      acc[headerId] = raw === '' || raw === null || raw === undefined ? '' : Number(raw);
      return acc;
    }

    acc[headerId] = `${values?.[headerId] ?? ''}`;
    return acc;
  }, {});

  return resolveRowFormulas(headers, rawValues);
};

const assertSheetOwner = async (sheetId, userId) => {
  const sheetSnap = await getDoc(doc(db, SHEETS_COLLECTION, sheetId));
  if (!sheetSnap.exists()) {
    throw new Error('Linked sheet not found.');
  }
  const sheetData = sheetSnap.data() || {};
  if (sheetData.userId === userId) {
    return { id: sheetSnap.id, ...sheetData };
  }

  const workspaceId = `${sheetData.workspaceId || ''}`.trim();
  const userProfile = await userService.getUserProfile(userId).catch(() => null);
  const isPlatformAdmin = `${userProfile?.role || ''}`.trim() === 'admin';
  if (sheetData.adminBuilderAccessEnabled === true && isPlatformAdmin) {
    return { id: sheetSnap.id, ...sheetData };
  }

  if (workspaceId) {
    const workspaceSnap = await getDoc(doc(db, 'docs-sheet-workspaces', workspaceId));
    const workspaceData = workspaceSnap.data() || {};
    if (workspaceSnap.exists() && (
      workspaceData.userId === userId ||
      (isPlatformAdmin && workspaceData.adminBuilderAccessEnabled === true)
    )) {
      return { id: sheetSnap.id, ...sheetData };
    }
  }

  throw new Error('Only the sheet owner, workspace owner, or an enabled platform admin can manage this form.');
};

const buildSheetFormPayload = ({ sheetId, sheetSnapshot, config, ownerId }) => {
  const allSheetHeaders = Array.isArray(sheetSnapshot?.headers) ? sheetSnapshot.headers : [];
  const linkedSheetVisibility = sanitizeSheetVisibility(sheetSnapshot?.visibility);
  const submissionMode = sanitizeSubmissionMode(config?.submissionMode);
  const modules = submissionMode === 'modular'
    ? normalizeModules(config?.modules, allSheetHeaders, config)
    : [];
  const formLayout = normalizeFormLayoutItems(
    config?.formLayout,
    allSheetHeaders,
    config?.enabledHeaderIds,
    config?.requiredHeaderIds
  );
  const enabledHeaderIds = formLayout
    .filter((item) => item.type === 'field')
    .map((item) => item.headerId);
  const requiredHeaderIds = normalizeHeaderIds(
    [
      ...(config?.requiredHeaderIds || []),
      ...formLayout.filter((item) => item.type === 'field' && item.required).map((item) => item.headerId)
    ],
    enabledHeaderIds
  );
  const moduleRepeatableHeaderIds = submissionMode === 'modular'
    ? modules.find((module) => module.repeatable)?.fieldIds || []
    : [];
  const repeatableLayout = submissionMode === 'modular'
    ? normalizeFormLayoutItems(
        config?.repeatableLayout,
        allSheetHeaders,
        moduleRepeatableHeaderIds,
        config?.repeatableRequiredHeaderIds,
        false
      )
    : [];
  const repeatableHeaderIds = submissionMode === 'modular'
    ? repeatableLayout
      .filter((item) => item.type === 'field')
      .map((item) => item.headerId)
    : [];
  const repeatableRequiredHeaderIds = submissionMode === 'modular'
    ? normalizeRepeatableRequiredHeaderIds(config?.repeatableRequiredHeaderIds, repeatableHeaderIds)
    : [];
  const repeatableFieldWidths = submissionMode === 'modular'
    ? normalizeRepeatableFieldWidths(
        {
          ...(Array.isArray(config?.repeatableFields)
            ? config.repeatableFields.reduce((acc, field) => {
                const headerId = `${field?.id || ''}`.trim();
                if (headerId) acc[headerId] = field?.formWidth;
                return acc;
              }, {})
            : {}),
          ...(config?.repeatableFieldWidths && typeof config.repeatableFieldWidths === 'object' ? config.repeatableFieldWidths : {})
        },
        repeatableHeaderIds
      )
    : {};
  const minimumRepeatableRows = submissionMode === 'modular'
    ? normalizeMinimumRepeatableRows(modules.find((module) => module.repeatable)?.repeatConfig?.minRows)
    : 1;
  const formFieldConfigById = new Map(
    formLayout
      .filter((item) => item.type === 'field')
      .map((item) => [item.headerId, item])
  );
  const fields = enabledHeaderIds
    .map((headerId) => allSheetHeaders.find((header) => header.id === headerId))
    .filter(Boolean)
    .map((header) => ({
      ...header,
      required: requiredHeaderIds.includes(header.id),
      formWidth: normalizeFormFieldWidth(formFieldConfigById.get(header.id)?.width)
    }));
  const repeatableFields = repeatableHeaderIds
    .map((headerId) => allSheetHeaders.find((header) => header.id === headerId))
    .filter(Boolean)
    .map((header) => ({
      ...header,
      required: repeatableRequiredHeaderIds.includes(header.id),
      formWidth: normalizeFormFieldWidth(repeatableFieldWidths[header.id])
    }));

  if (!fields.length && !repeatableFields.length) {
    throw new Error('Select at least one form field.');
  }
  if (submissionMode === 'modular' && !repeatableFields.length) {
    throw new Error('Select at least one repeatable field for modular mode.');
  }

  return stripUndefinedDeep({
    id: sheetId,
    linkedSheetId: sheetId,
    linkedSheetTitle: `${sheetSnapshot?.title || ''}`.trim(),
    linkedSheetVisibility,
    logoUrl: normalizeFormLogoUrl(config?.logoUrl),
    ogImageUrl: normalizeFormOgImageUrl(sheetSnapshot?.ogImageUrl),
    primaryColor: normalizeSheetThemeColor(sheetSnapshot?.primaryColor),
    secondaryColor: normalizeSheetThemeColor(sheetSnapshot?.secondaryColor),
    renderFont: normalizeSheetRenderFont(sheetSnapshot?.renderFont),
    workspaceId: `${sheetSnapshot?.workspaceId || ''}`.trim() || undefined,
    userId: ownerId,
    status: 'active',
    submissionMode,
    accessMode: getEffectiveFormAccessMode(linkedSheetVisibility, config?.accessMode),
    showShellHeader: config?.showShellHeader !== false,
    showFormIntro: config?.showFormIntro !== false,
    title: `${config?.title || sheetSnapshot?.title || 'Untitled form'}`.trim(),
    description: `${config?.description || sheetSnapshot?.description || ''}`.trim(),
    submitLabel: `${config?.submitLabel || 'Submit'}`.trim() || 'Submit',
    successTitle: `${config?.successTitle || 'Submission received'}`.trim() || 'Submission received',
    successMessage: `${config?.successMessage || 'Thanks. Your response has been captured.'}`.trim() || 'Thanks. Your response has been captured.',
    ...getFormAvailability(config),
    allowDrafts: submissionMode === 'modular' ? config?.allowDrafts === true : false,
    prefillSelectorConfig: (() => {
      const normalizedSelectorConfig = normalizeFormPrefillSelectorConfig(config?.prefillSelectorConfig);
      return normalizedSelectorConfig.enabled && normalizedSelectorConfig.sourceSheetId && normalizedSelectorConfig.actionId
        ? normalizedSelectorConfig
        : undefined;
    })(),
    enabledHeaderIds,
    requiredHeaderIds,
    repeatableHeaderIds,
    repeatableRequiredHeaderIds,
    repeatableFieldWidths,
    minimumRepeatableRows,
    repeatableEntryMode: submissionMode === 'modular' ? sanitizeRepeatableEntryMode(modules.find((module) => module.repeatable)?.repeatConfig?.entryMode) : undefined,
    repeatableAddPrompt: submissionMode === 'modular' ? modules.find((module) => module.repeatable)?.repeatConfig?.addPrompt : undefined,
    repeatableAddLabel: submissionMode === 'modular' ? modules.find((module) => module.repeatable)?.repeatConfig?.addLabel : undefined,
    repeatableDoneLabel: submissionMode === 'modular' ? modules.find((module) => module.repeatable)?.repeatConfig?.doneLabel : undefined,
    modules: submissionMode === 'modular' ? modules : undefined,
    repeatableLayout: submissionMode === 'modular'
      ? repeatableLayout.map((item) => (
          item.type === 'field'
            ? {
                id: item.id,
                type: 'field',
                headerId: item.headerId,
                width: normalizeFormFieldWidth(item.width)
              }
            : item.type === 'sectionDivider'
              ? {
                  id: item.id,
                  type: 'sectionDivider',
                  label: normalizeSectionDividerLabel(item)
                }
              : item.type === 'textBlock'
                ? {
                    id: item.id,
                    type: 'textBlock',
                    contentHtml: `${item.contentHtml || item.content || ''}`,
                    alignment: normalizeTextAlign(item.alignment)
                  }
                : item.type === 'imageSlide'
                  ? {
                      id: item.id,
                      type: 'imageSlide',
                      slides: normalizeSlideItems(item.slides),
                      slideMode: normalizeSlideMode(item.slideMode),
                      autoAdvanceSeconds: normalizeSlideIntervalSeconds(item.autoAdvanceSeconds)
                    }
                  : {
                      id: item.id,
                      type: 'pageBreak'
                    }
        ))
      : undefined,
    formLayout: formLayout.map((item) => (
      item.type === 'field'
        ? {
            id: item.id,
            type: 'field',
            headerId: item.headerId,
            width: normalizeFormFieldWidth(item.width)
          }
        : item.type === 'sectionDivider'
          ? {
              id: item.id,
              type: 'sectionDivider',
              label: normalizeSectionDividerLabel(item)
            }
          : item.type === 'textBlock'
            ? {
                id: item.id,
                type: 'textBlock',
                contentHtml: `${item.contentHtml || item.content || ''}`,
                alignment: normalizeTextAlign(item.alignment)
              }
            : item.type === 'imageSlide'
              ? {
                  id: item.id,
                  type: 'imageSlide',
                  slides: normalizeSlideItems(item.slides),
                  slideMode: normalizeSlideMode(item.slideMode),
                  autoAdvanceSeconds: normalizeSlideIntervalSeconds(item.autoAdvanceSeconds)
                }
              : {
              id: item.id,
              type: 'pageBreak'
              }
    )),
    fields,
    repeatableFields,
    sheetHeaders: allSheetHeaders,
    updatedAt: serverTimestamp()
  });
};

export const sheetFormService = {
  getDefaultConfig(headers = [], sheetVisibility = 'public') {
    return {
      enabled: false,
      accessMode: getEffectiveFormAccessMode(sheetVisibility, 'public'),
      showShellHeader: true,
      showFormIntro: true,
      logoUrl: '',
      title: '',
      description: '',
      submitLabel: 'Submit',
      successTitle: 'Submission received',
      successMessage: 'Thanks. Your response has been captured.',
      availabilityStartAt: '',
      availabilityEndAt: '',
      submissionMode: 'singleRow',
      allowDrafts: false,
      prefillSelectorConfig: {
        enabled: false,
        sourceSheetId: '',
        actionId: ''
      },
      enabledHeaderIds: getDefaultEnabledHeaderIds(headers),
      requiredHeaderIds: [],
      repeatableHeaderIds: [],
      repeatableRequiredHeaderIds: [],
      repeatableFieldWidths: {},
      minimumRepeatableRows: 1,
      repeatableEntryMode: 'stepper',
      repeatableAddPrompt: 'Add another row?',
      repeatableAddLabel: 'Add another row',
      repeatableDoneLabel: 'Continue',
      modules: buildDefaultModules(headers),
      repeatableLayout: [],
      formLayout: getDefaultFormLayout(headers)
    };
  },

  getDefaultLayout(headers = []) {
    return getDefaultFormLayout(headers);
  },

  normalizeFormLayout(layout = [], headers = [], enabledHeaderIds = [], requiredHeaderIds = [], requireFieldFallback = true) {
    return normalizeFormLayoutItems(layout, headers, enabledHeaderIds, requiredHeaderIds, requireFieldFallback);
  },

  async getSheetFormById(formId) {
    const formSnap = await getDoc(doc(db, SHEET_FORMS_COLLECTION, formId));
    if (!formSnap.exists()) {
      return null;
    }

    return {
      id: formSnap.id,
      ...formSnap.data()
    };
  },

  async getSheetFormSummaries(sheetIds = []) {
    const normalizedSheetIds = Array.from(new Set(
      (Array.isArray(sheetIds) ? sheetIds : [])
        .map((sheetId) => `${sheetId || ''}`.trim())
        .filter(Boolean)
    ));

    if (!normalizedSheetIds.length) {
      return {};
    }

    const forms = await Promise.allSettled(
      normalizedSheetIds.map(async (sheetId) => {
        const formSnap = await getDoc(doc(db, SHEET_FORMS_COLLECTION, sheetId));
        if (!formSnap.exists()) {
          return null;
        }

        const data = formSnap.data() || {};
        return {
          id: formSnap.id,
          linkedSheetId: `${data.linkedSheetId || formSnap.id}`.trim() || formSnap.id,
          title: `${data.title || ''}`.trim(),
          accessMode: sanitizeAccessMode(data.accessMode),
          status: `${data.status || 'active'}`.trim() || 'active'
        };
      })
    );

    return forms.reduce((acc, result) => {
      if (result.status !== 'fulfilled') return acc;
      const form = result.value;
      if (!form?.id) return acc;
      acc[form.id] = form;
      return acc;
    }, {});
  },

  async saveSheetForm(sheetId, sheetSnapshot, config, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) {
      throw new Error('User is required to publish a sheet form.');
    }

    const managedSheet = await assertSheetOwner(sheetId, ownerId);
    const formOwnerId = `${managedSheet?.userId || ''}`.trim() || ownerId;

    const payload = buildSheetFormPayload({
      sheetId,
      sheetSnapshot,
      config,
      ownerId: formOwnerId
    });

    await setDoc(doc(db, SHEET_FORMS_COLLECTION, sheetId), {
      ...payload,
      createdAt: serverTimestamp()
    }, { merge: true });
    await updateDoc(doc(db, SHEETS_COLLECTION, sheetId), {
      publicFormUploadsEnabled: payload.accessMode === 'public',
      updatedAt: serverTimestamp()
    });

    return {
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async deleteSheetForm(sheetId, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) {
      throw new Error('User is required to disable a sheet form.');
    }

    await assertSheetOwner(sheetId, ownerId);
    await deleteDoc(doc(db, SHEET_FORMS_COLLECTION, sheetId));
    await updateDoc(doc(db, SHEETS_COLLECTION, sheetId), {
      publicFormUploadsEnabled: false,
      updatedAt: serverTimestamp()
    });
  },

  async getSheetFormDraft(formId, userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) {
      return null;
    }

    const draftSnap = await getDoc(doc(db, SHEET_FORM_DRAFTS_COLLECTION, buildSheetFormDraftId(formId, currentUserId)));
    if (!draftSnap.exists()) {
      return null;
    }

    return {
      id: draftSnap.id,
      ...draftSnap.data()
    };
  },

  async saveSheetFormDraft(formId, values = {}, navigationState = {}, user) {
    const currentUser = user || auth.currentUser || null;
    const userId = currentUser?.uid;
    if (!userId) {
      throw new Error('Sign in to save this draft.');
    }

    const formData = await this.getSheetFormById(formId);
    if (!formData) {
      throw new Error('Form not found.');
    }
    if (sanitizeSubmissionMode(formData?.submissionMode) !== 'modular') {
      throw new Error('Drafts are only available for modular forms.');
    }
    if (formData.allowDrafts !== true) {
      throw new Error('Draft saving is not enabled for this form.');
    }

    const payload = stripUndefinedDeep({
      formId,
      linkedSheetId: formData.linkedSheetId,
      userId,
      values,
      navigationState: {
        currentPageIndex: Math.max(0, Number(navigationState?.currentPageIndex) || 0),
        currentDetailRowIndex: Math.max(0, Number(navigationState?.currentDetailRowIndex) || 0)
      },
      status: 'draft',
      updatedAt: serverTimestamp()
    });

    await setDoc(
      doc(db, SHEET_FORM_DRAFTS_COLLECTION, buildSheetFormDraftId(formId, userId)),
      {
        ...payload,
        createdAt: serverTimestamp()
      },
      { merge: true }
    );

    return {
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async deleteSheetFormDraft(formId, userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) {
      return;
    }

    await deleteDoc(doc(db, SHEET_FORM_DRAFTS_COLLECTION, buildSheetFormDraftId(formId, currentUserId)));
  },

  createInitialValues(formData) {
    const fields = Array.isArray(formData?.fields) ? formData.fields : [];
    const submissionMode = sanitizeSubmissionMode(formData?.submissionMode);

    if (submissionMode !== 'modular') {
      return fields.reduce((acc, field) => {
        acc[field.id] = getFieldInitialValue(field);
        return acc;
      }, {});
    }

    const repeatableFields = Array.isArray(formData?.repeatableFields) ? formData.repeatableFields : [];
    const minimumRepeatableRows = normalizeMinimumRepeatableRows(formData?.minimumRepeatableRows);
    const shared = fields.reduce((acc, field) => {
      acc[field.id] = getFieldInitialValue(field);
      return acc;
    }, {});
    const createRepeatableRow = () => repeatableFields.reduce((acc, field) => {
      acc[field.id] = getFieldInitialValue(field);
      return acc;
    }, {});

    return {
      shared,
      rows: Array.from({ length: minimumRepeatableRows }).map(() => createRepeatableRow())
    };
  },

  async submitSheetForm(formId, values = {}, user) {
    const formData = await this.getSheetFormById(formId);
    if (!formData) {
      throw new Error('Form not found.');
    }

    const currentUser = user || auth.currentUser || null;
    const createdBy = currentUser?.uid || null;
    const createdByEmail = currentUser?.email || null;
    const submissionMode = sanitizeSubmissionMode(formData?.submissionMode);
    const allHeaders = Array.isArray(formData.sheetHeaders) ? formData.sheetHeaders : [];
    const availabilityState = getFormAvailabilityState(formData);
    if (availabilityState === 'not_started') {
      throw new Error('This form is not available yet.');
    }
    if (availabilityState === 'ended') {
      throw new Error('This form is no longer available.');
    }

    if (submissionMode !== 'modular') {
      const sanitizedValues = sanitizeSubmissionValues(values, allHeaders);

      const rowRef = await addDoc(
        collection(db, SHEETS_COLLECTION, formData.linkedSheetId, SHEET_ITEMS_SUBCOLLECTION),
        stripUndefinedDeep({
          sheetId: formData.linkedSheetId,
          sourceFormId: formId,
          sourceFormAccessMode: sanitizeAccessMode(formData.accessMode),
          values: sanitizedValues,
          createdBy,
          createdByEmail,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      );

      return {
        id: rowRef.id,
        sheetId: formData.linkedSheetId,
        sourceFormId: formId,
        values: sanitizedValues,
        createdBy,
        createdByEmail,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }

    const sharedValues = values?.shared && typeof values.shared === 'object' ? values.shared : {};
    const rawRows = Array.isArray(values?.rows) ? values.rows : [];
    const sanitizedRows = rawRows
      .map((rowValues) => sanitizeSubmissionValues({ ...sharedValues, ...(rowValues || {}) }, allHeaders))
      .filter((rowValues) => Object.values(rowValues).some((value) => (
        Array.isArray(value)
          ? value.length > 0
          : value !== '' && value !== false && value !== null && value !== undefined
      )));

    if (!sanitizedRows.length) {
      throw new Error('Add at least one populated row before submitting.');
    }

    const rowRefs = await Promise.all(
      sanitizedRows.map((sanitizedValues) => addDoc(
        collection(db, SHEETS_COLLECTION, formData.linkedSheetId, SHEET_ITEMS_SUBCOLLECTION),
        stripUndefinedDeep({
          sheetId: formData.linkedSheetId,
          sourceFormId: formId,
          sourceFormAccessMode: sanitizeAccessMode(formData.accessMode),
          values: sanitizedValues,
          createdBy,
          createdByEmail,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      ))
    );

    return {
      ids: rowRefs.map((rowRef) => rowRef.id),
      count: rowRefs.length,
      sheetId: formData.linkedSheetId,
      sourceFormId: formId,
      createdBy,
      createdByEmail,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }
};
